import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2, UserX, Info, BellRing } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const WINDOWS = [30, 60, 90, 180, 365];

const TABS = [
  ["inactive", "Quiet", "Signed in before, nothing since your chosen window"],
  ["never_logged_in", "Never used", "Given a password or an invite but never signed in"],
  ["never_activated", "Imported contacts", "No password, never invited — directory records, not really stale"],
];

export const StaleAccountsDialog = ({ open, onOpenChange, canDelete, onChanged }) => {
  const [days, setDays] = useState(90);
  const [tab, setTab] = useState("inactive");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [removing, setRemoving] = useState(false);

  const load = useCallback((d) => {
    setLoading(true);
    setPicked(new Set());
    api.get(`/users/stale?days=${d}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load stale accounts"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(days); }, [open, days, load]);

  const rows = data?.[tab] || [];
  const toggle = (id) => setPicked((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const nudge = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.user_id}/nudge`);
      if (data.status === "paused") toast.warning(`Reminder for ${u.email} was blocked by the outbound email pause`);
      else toast.success(`Reminder ${data.nudge} of 4 sent to ${u.email}`);
      load(days);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the reminder");
    }
  };

  const removePicked = async () => {
    const ids = [...picked];
    if (!window.confirm(`Permanently remove ${ids.length} account(s)? This cannot be undone.`)) return;
    setRemoving(true);
    let ok = 0;
    const failed = [];
    for (const id of ids) {
      try { await api.delete(`/users/${id}`); ok += 1; } catch { failed.push(id); }
    }
    setRemoving(false);
    if (ok) toast.success(`Removed ${ok} account(s)`);
    if (failed.length) toast.error(`${failed.length} could not be removed`);
    onChanged?.();
    load(days);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="stale-accounts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserX className="h-4 w-4 text-rose-600" /> Stale accounts
          </DialogTitle>
          <DialogDescription className="text-xs">
            {data ? `${data.scanned.toLocaleString()} accounts scanned · ${data.total} look stale` : "Scanning…"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="stale-window"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WINDOWS.map((d) => <SelectItem key={d} value={String(d)}>Quiet {d}+ days</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
            {TABS.map(([key, label]) => (
              <button key={key} onClick={() => { setTab(key); setPicked(new Set()); }} data-testid={`stale-tab-${key}`}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  tab === key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {label} ({(data?.[key] || []).length})
              </button>
            ))}
          </div>
          {canDelete && picked.size > 0 && (
            <Button size="sm" variant="destructive" onClick={removePicked} disabled={removing}
              className="ml-auto" data-testid="stale-remove-selected">
              {removing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Remove {picked.size}
            </Button>
          )}
        </div>

        <p className="flex items-start gap-1.5 text-[11px] text-slate-400">
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          {TABS.find(([k]) => k === tab)?.[2]}
        </p>

        <div className="max-h-[50vh] overflow-auto rounded-lg border border-slate-200">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <p className="p-6 text-center text-xs text-slate-400" data-testid="stale-empty">Nothing in this bucket.</p>
          ) : (
            <table className="w-full text-xs" data-testid="stale-table">
              <thead className="sticky top-0 bg-[#33456b] text-white">
                <tr>
                  {canDelete && <th className="w-8 px-2.5 py-1.5" />}
                  {["Name", "Email", "Role", "Organisation", "Last seen", "Age", ""].map((h, i) => (
                    <th key={i} className="px-2.5 py-1.5 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((u, i) => (
                  <tr key={u.user_id} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`stale-row-${u.user_id}`}>
                    {canDelete && (
                      <td className="px-2.5 py-1.5">
                        <input type="checkbox" checked={picked.has(u.user_id)} onChange={() => toggle(u.user_id)}
                          data-testid={`stale-pick-${u.user_id}`} className="h-3.5 w-3.5 cursor-pointer accent-[#206bc4]" />
                      </td>
                    )}
                    <td className="px-2.5 py-1.5 font-medium text-slate-700">{u.name}</td>
                    <td className="px-2.5 py-1.5 text-slate-500">{u.email}</td>
                    <td className="px-2.5 py-1.5 text-slate-600">{u.role_label || u.role}</td>
                    <td className="px-2.5 py-1.5 text-slate-500">{u.org_name || "—"}</td>
                    <td className="px-2.5 py-1.5 text-slate-500">
                      {u.last_login ? u.last_login.slice(0, 10) : <span className="text-slate-400">never</span>}
                    </td>
                    <td className="px-2.5 py-1.5 text-slate-600">{u.age_days ?? "—"}d</td>
                    <td className="px-2.5 py-1.5 text-right">
                      {canDelete && !u.last_login && (
                        <Button variant="ghost" size="sm" onClick={() => nudge(u)} data-testid={`stale-nudge-${u.user_id}`}
                          className="h-6 px-1.5 text-[11px] text-slate-500 hover:text-primary" title="Send a fresh invite link">
                          <BellRing className="h-3 w-3" /> Nudge
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StaleAccountsDialog;
